/**
 * US Phase-1 rollout geo controller.
 * Shows messaging only to US visitors outside configured supported states.
 * Fails open on lookup errors (no blocking).
 */
(function () {
  'use strict';

  if (window.__piUsRolloutInitialized) return;
  window.__piUsRolloutInitialized = true;

  const STORAGE_KEY = 'pi_us_rollout_geo_v3';
  const TTL_MS = 24 * 60 * 60 * 1000;
  const POPUP_SHOWN_KEY = 'pi_us_rollout_popup_shown_v1';
  const ROLLOUT_POPUP_ATTR = 'data-us-rollout-popup';
  const POPUP_LOCK_CLASSES = [
    'modal-open',
    'overflow-hidden',
    'klaviyo-open',
    'klaviyo-prevent-body-scrolling',
    'no-scroll',
  ];

  /** @returns {Record<string, unknown>|null} */
  function getConfig() {
    const el = document.getElementById('us-rollout-config');
    if (!el || !el.textContent) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  /** @param {string} value */
  function normalizeState(value) {
    return String(value || '')
      .trim()
      .toUpperCase()
      .replace(/^US-/, '');
  }

  /** @type {Record<string, string>} */
  const US_STATE_NAME_TO_CODE = {
    ALABAMA: 'AL',
    ALASKA: 'AK',
    ARIZONA: 'AZ',
    ARKANSAS: 'AR',
    CALIFORNIA: 'CA',
    COLORADO: 'CO',
    CONNECTICUT: 'CT',
    DELAWARE: 'DE',
    'DISTRICT OF COLUMBIA': 'DC',
    FLORIDA: 'FL',
    GEORGIA: 'GA',
    HAWAII: 'HI',
    IDAHO: 'ID',
    ILLINOIS: 'IL',
    INDIANA: 'IN',
    IOWA: 'IA',
    KANSAS: 'KS',
    KENTUCKY: 'KY',
    LOUISIANA: 'LA',
    MAINE: 'ME',
    MARYLAND: 'MD',
    MASSACHUSETTS: 'MA',
    MICHIGAN: 'MI',
    MINNESOTA: 'MN',
    MISSISSIPPI: 'MS',
    MISSOURI: 'MO',
    MONTANA: 'MT',
    NEBRASKA: 'NE',
    NEVADA: 'NV',
    'NEW HAMPSHIRE': 'NH',
    'NEW JERSEY': 'NJ',
    'NEW MEXICO': 'NM',
    'NEW YORK': 'NY',
    'NORTH CAROLINA': 'NC',
    'NORTH DAKOTA': 'ND',
    OHIO: 'OH',
    OKLAHOMA: 'OK',
    OREGON: 'OR',
    PENNSYLVANIA: 'PA',
    'RHODE ISLAND': 'RI',
    'SOUTH CAROLINA': 'SC',
    'SOUTH DAKOTA': 'SD',
    TENNESSEE: 'TN',
    TEXAS: 'TX',
    UTAH: 'UT',
    VERMONT: 'VT',
    VIRGINIA: 'VA',
    WASHINGTON: 'WA',
    'WEST VIRGINIA': 'WV',
    WISCONSIN: 'WI',
    WYOMING: 'WY',
  };

  /** @param {string} value */
  function normalizeCountryCode(value) {
    const code = normalizeState(value);
    if (!code) return '';
    if (code === 'UNITED STATES' || code === 'USA' || code === 'U.S.A.' || code === 'U.S.') {
      return 'US';
    }
    return code.length === 2 ? code : code;
  }

  /** @param {string} value */
  function normalizeRegionCode(value) {
    const code = normalizeState(value);
    if (!code) return '';
    if (code.length === 2) return code;
    return US_STATE_NAME_TO_CODE[code] || code;
  }

  /** @param {{country: string, state: string}|null} geo */
  function isCompleteUsGeo(geo) {
    return Boolean(geo && geo.country === 'US' && geo.state && geo.state.length === 2);
  }

  function clearRolloutCachesIfRequested() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('rollout_reset') !== '1') return;
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem('pi_us_rollout_geo_v2');
      localStorage.removeItem('pi_us_rollout_geo_v1');
      sessionStorage.removeItem(POPUP_SHOWN_KEY);
      localStorage.removeItem(POPUP_SHOWN_KEY);
    } catch {
      /* ignore */
    }
  }

  /** QA / BrowserStack: ?rollout_geo=US-MO simulates visitor location (optional ?rollout_reset=1). */
  function getQueryGeoOverride() {
    try {
      const raw = new URLSearchParams(window.location.search).get('rollout_geo');
      if (!raw) return null;
      const parts = String(raw).split('-').filter(Boolean);
      if (!parts.length) return null;
      const country = normalizeCountryCode(parts[0]);
      const state = normalizeRegionCode(parts.slice(1).join('-'));
      if (!country) return null;
      return { country, state };
    } catch {
      return null;
    }
  }

  async function fetchShopifyCountryGeo() {
    const root =
      (typeof window.Shopify !== 'undefined' &&
        window.Shopify.routes &&
        window.Shopify.routes.root) ||
      '/';
    const url = `${root}browsing_context_suggestions.json?country[enabled]=true`;
    const res = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) throw new Error(`shopify_geo_http_${res.status}`);
    const data = await res.json();
    const detected = data?.detected_values?.country;
    const handle = detected?.handle || detected?.iso_code || '';
    const country = normalizeCountryCode(handle);
    if (!country) return null;
    return { country, state: '' };
  }

  /** @param {string} raw */
  function parseSupportedStates(raw) {
    const set = new Set();
    String(raw || '')
      .split(/[,|\s]+/)
      .map(normalizeState)
      .filter(Boolean)
      .forEach((code) => set.add(code));
    return set;
  }

  /** @returns {{country: string, state: string}|null} */
  function readCachedGeo() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.ts || Date.now() - parsed.ts > TTL_MS) return null;
      const geo = {
        country: normalizeCountryCode(parsed.country),
        state: normalizeRegionCode(parsed.state),
      };
      if (geo.country === 'US' && !geo.state) return null;
      return geo;
    } catch {
      return null;
    }
  }

  /** @param {{country: string, state: string}} geo */
  function writeCachedGeo(geo) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          country: geo.country,
          state: geo.state,
          ts: Date.now(),
        }),
      );
    } catch {
      /* ignore */
    }
  }

  const GEO_FALLBACK_ENDPOINT = 'https://ipapi.co/json/';

  /** @param {unknown} data */
  function parseGeoPayload(data) {
    if (!data || typeof data !== 'object') return null;
    const record = /** @type {Record<string, unknown>} */ (data);
    if (record.error === true || record.error || record.success === false) return null;

    const country = normalizeCountryCode(
      record.country_code || record.countryCode || record.country || '',
    );
    const state = normalizeRegionCode(
      record.region_code || record.regionCode || record.region || record.state || '',
    );

    if (!country) return null;
    return { country, state };
  }

  /**
   * @param {string} endpoint
   * @param {Record<string, string>} headers
   */
  async function fetchGeoFromEndpoint(endpoint, headers) {
    const res = await fetch(endpoint, { cache: 'no-store', headers });
    if (!res.ok) throw new Error(`geo_http_${res.status}`);

    const data = await res.json();
    return parseGeoPayload(data);
  }

  /**
   * @param {{country: string, state: string}} a
   * @param {{country: string, state: string}} b
   */
  function pickBetterGeo(a, b) {
    if (!a) return b;
    if (!b) return a;
    if (isCompleteUsGeo(a) && !isCompleteUsGeo(b)) return a;
    if (isCompleteUsGeo(b) && !isCompleteUsGeo(a)) return b;
    if (a.state && !b.state) return a;
    if (b.state && !a.state) return b;
    return a;
  }

  /** @param {Record<string, unknown>} config */
  async function fetchGeo(config) {
    clearRolloutCachesIfRequested();

    const queryOverride = getQueryGeoOverride();
    if (queryOverride) {
      return queryOverride;
    }

    const cached = readCachedGeo();
    if (cached) return cached;

    const endpoint = String(config.geoEndpoint || 'https://ipapi.co/json/').trim();

    const headers = {};
    const apiKey = String(config.geoApiKey || '').trim();
    if (apiKey) {
      headers.Authorization = `Token ${apiKey}`;
    }

    const endpoints = [];
    if (endpoint) endpoints.push(endpoint);
    const normalizedPrimary = endpoint.replace(/\/+$/, '');
    const normalizedFallback = GEO_FALLBACK_ENDPOINT.replace(/\/+$/, '');
    if (!normalizedPrimary || normalizedPrimary !== normalizedFallback) {
      endpoints.push(GEO_FALLBACK_ENDPOINT);
    }

    let bestGeo = null;
    for (const url of endpoints) {
      try {
        const geo = await fetchGeoFromEndpoint(url, headers);
        if (!geo) continue;
        bestGeo = pickBetterGeo(bestGeo, geo);
        if (isCompleteUsGeo(geo)) {
          writeCachedGeo(geo);
          return geo;
        }
      } catch {
        /* try next endpoint */
      }
    }

    if (bestGeo && bestGeo.country !== 'US') {
      writeCachedGeo(bestGeo);
      return bestGeo;
    }

    if (bestGeo && isCompleteUsGeo(bestGeo)) {
      writeCachedGeo(bestGeo);
      return bestGeo;
    }

    try {
      const shopifyCountry = await fetchShopifyCountryGeo();
      if (shopifyCountry && shopifyCountry.country !== 'US') {
        writeCachedGeo(shopifyCountry);
        return shopifyCountry;
      }
    } catch {
      /* ignore */
    }

    if (bestGeo && bestGeo.state) {
      writeCachedGeo(bestGeo);
      return bestGeo;
    }

    return null;
  }

  /**
   * @param {{country: string, state: string}|null} geo
   * @param {Set<string>} supportedStates
   */
  function shouldShowRollout(geo, supportedStates) {
    if (!geo || !geo.country) return false;
    if (geo.country !== 'US') return false;
    if (!geo.state) return false;
    return !supportedStates.has(geo.state);
  }

  /** @param {string} formId */
  function openKlaviyoForm(formId) {
    if (!formId) return;
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', formId]);
  }

  /** @param {string} formId */
  function closeKlaviyoForm(formId) {
    if (!formId) return;
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['closeForm', formId]);
  }

  const KLAVIYO_POPUP_DIALOG = '[role="dialog"][aria-label*="POPUP" i]';
  const ROLLOUT_POPUP_DIALOG = `${KLAVIYO_POPUP_DIALOG}[${ROLLOUT_POPUP_ATTR}="true"]`;

  let pendingCloseTimers = [];
  let pendingCleanupTimers = [];

  /** @param {string} formId */
  function getRolloutFormSelector(formId) {
    return formId ? `[class*="klaviyo-form-${formId}"]` : '';
  }

  /** @param {string} formId */
  function getRolloutPopupSelector(formId) {
    const selectors = [ROLLOUT_POPUP_DIALOG];
    const formSelector = getRolloutFormSelector(formId);
    if (formSelector) selectors.push(formSelector);
    return selectors.join(', ');
  }

  /**
   * Klaviyo popup markup is not stable: some onsite forms include the form id in
   * serialized config instead of a wrapper class. Keep ownership tied to the
   * configured rollout form id, never to generic POPUP labels.
   *
   * @param {Element} dialog
   * @param {string} formId
   */
  function isRolloutPopupDialog(dialog, formId) {
    if (dialog.matches(ROLLOUT_POPUP_DIALOG)) return true;
    if (!formId) return false;

    const formSelector = getRolloutFormSelector(formId);
    return Boolean(
      (formSelector && dialog.querySelector(formSelector)) ||
        String(dialog.outerHTML || '').includes(formId),
    );
  }

  /** @param {string} formId */
  function markRolloutPopupDialogs(formId) {
    if (!formId) return;

    document.querySelectorAll(KLAVIYO_POPUP_DIALOG).forEach((dialog) => {
      if (isRolloutPopupDialog(dialog, formId)) {
        dialog.setAttribute(ROLLOUT_POPUP_ATTR, 'true');
      }
    });
  }

  /** @param {Element|null} el @param {string} formId */
  function isInsideRolloutPopup(el, formId) {
    if (!el) return false;
    return Boolean(el.closest(getRolloutPopupSelector(formId)));
  }

  /** @param {Element} el */
  function isPopupCloseControl(el) {
    return Boolean(
      el.closest(
        '[aria-label*="close" i], button[class*="close" i], [data-testid*="close" i]',
      ),
    );
  }

  /** @param {Element} el */
  function isPopupSubmitControl(el) {
    if (isPopupCloseControl(el)) return false;
    if (el.closest('button[type="submit"], input[type="submit"]')) return true;
    return Boolean(el.closest('[data-testid="form-row"] button[type="button"]'));
  }

  /** @param {Element} el */
  function isPopupBackdrop(el) {
    return Boolean(el.matches(KLAVIYO_POPUP_DIALOG));
  }

  /** @param {HTMLElement} el */
  function clearScrollLockStyles(el) {
    POPUP_LOCK_CLASSES.forEach((className) => el.classList.remove(className));

    if (el.style.overflow === 'hidden' || el.style.overflow === 'clip') {
      el.style.overflow = '';
    }

    if (el.style.overflowY === 'hidden' || el.style.overflowY === 'clip') {
      el.style.overflowY = '';
    }

    if (el.style.position === 'fixed') {
      el.style.position = '';
      el.style.top = '';
      el.style.left = '';
      el.style.right = '';
      el.style.width = '';
    }
  }

  function hasVisibleKlaviyoPopupDialog() {
    return Array.from(document.querySelectorAll(KLAVIYO_POPUP_DIALOG)).some((dialog) => {
      const style = window.getComputedStyle(dialog);
      const rect = dialog.getBoundingClientRect();
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        style.pointerEvents !== 'none' &&
        rect.width > 0 &&
        rect.height > 0
      );
    });
  }

  /** @param {Element} el */
  function removeOrHidePopupArtifact(el) {
    try {
      el.remove();
    } catch {
      if (el instanceof HTMLElement) {
        el.hidden = true;
        el.setAttribute('aria-hidden', 'true');
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
        el.style.setProperty('z-index', '-1', 'important');
      }
    }
  }

  /** @param {string} formId */
  function cleanupRolloutPopupArtifacts(formId) {
    if (!formId) return;

    document.querySelectorAll(KLAVIYO_POPUP_DIALOG).forEach((dialog) => {
      if (isRolloutPopupDialog(dialog, formId)) {
        removeOrHidePopupArtifact(dialog);
      }
    });

    if (!hasVisibleKlaviyoPopupDialog()) {
      clearScrollLockStyles(document.documentElement);
      clearScrollLockStyles(document.body);
    }
  }

  /** @param {number[]} timers */
  function clearTimers(timers) {
    timers.forEach((timer) => window.clearTimeout(timer));
    timers.length = 0;
  }

  /** @param {string} formId */
  function schedulePopupCleanup(formId) {
    clearTimers(pendingCleanupTimers);
    [50, 350, 1300, 2200].forEach((delay) => {
      pendingCleanupTimers.push(window.setTimeout(() => cleanupRolloutPopupArtifacts(formId), delay));
    });
  }

  /** @param {string} formId */
  function pushOpenRolloutPopup(formId) {
    openKlaviyoForm(formId);
    [0, 100, 300, 700, 1200].forEach((delay) => {
      window.setTimeout(() => markRolloutPopupDialogs(formId), delay);
    });
  }

  /** @param {string} formId */
  function prepareRolloutPopupOpen(formId) {
    clearTimers(pendingCloseTimers);
    clearTimers(pendingCleanupTimers);
    cleanupRolloutPopupArtifacts(formId);
  }

  /** @param {string} formId */
  function dismissRolloutPopup(formId) {
    markPopupDismissed();
    closeKlaviyoForm(formId);
    clearTimers(pendingCloseTimers);
    pendingCloseTimers.push(window.setTimeout(() => closeKlaviyoForm(formId), 300));
    pendingCloseTimers.push(window.setTimeout(() => closeKlaviyoForm(formId), 1200));
    schedulePopupCleanup(formId);
  }

  /** @param {string} formId */
  function closeKlaviyoFormWhenReady(formId) {
    if (!formId) return;

    let attempts = 0;
    const maxAttempts = 30;

    const tryClose = () => {
      attempts += 1;
      closeKlaviyoForm(formId);
      cleanupRolloutPopupArtifacts(formId);
    };

    tryClose();

    const timer = window.setInterval(() => {
      tryClose();
      if (attempts >= maxAttempts) {
        window.clearInterval(timer);
      }
    }, 500);
  }

  /** @param {string} formId */
  function openKlaviyoFormWhenReady(formId) {
    if (!formId) return;

    let attempts = 0;
    const maxAttempts = 50;

    prepareRolloutPopupOpen(formId);

    const tryOpen = () => {
      attempts += 1;
      pushOpenRolloutPopup(formId);
    };

    if (window._klOnsite || window.klaviyo) {
      tryOpen();
      const retryTimer = window.setInterval(() => {
        tryOpen();
        if (attempts >= 8) window.clearInterval(retryTimer);
      }, 400);
      return;
    }

    const timer = window.setInterval(() => {
      if (window._klOnsite || window.klaviyo) {
        window.clearInterval(timer);
        tryOpen();
        const retryTimer = window.setInterval(() => {
          tryOpen();
          if (attempts >= 8) window.clearInterval(retryTimer);
        }, 400);
      } else if (attempts >= maxAttempts) {
        window.clearInterval(timer);
        tryOpen();
      }
    }, 200);
  }

  /** @returns {{ dismissed: boolean }|null} */
  function readPopupState() {
    try {
      const raw = sessionStorage.getItem(POPUP_SHOWN_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.dismissed !== true) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  function hasPopupBeenDismissed() {
    const state = readPopupState();
    return Boolean(state && state.dismissed);
  }

  function markPopupDismissed() {
    const payload = JSON.stringify({
      dismissed: true,
      ts: Date.now(),
    });

    try {
      sessionStorage.setItem(POPUP_SHOWN_KEY, payload);
    } catch {
      /* ignore */
    }
  }

  /** @param {string} formId */
  function watchPopupDismiss(formId) {
    if (!formId || watchPopupDismiss.bound) return;
    watchPopupDismiss.bound = true;

    document.addEventListener(
      'click',
      (event) => {
        const target = /** @type {Element|null} */ (event.target instanceof Element ? event.target : null);
        if (!target || !isInsideRolloutPopup(target, formId)) return;

        if (isPopupCloseControl(target)) {
          dismissRolloutPopup(formId);
          return;
        }

        if (isPopupBackdrop(target)) {
          dismissRolloutPopup(formId);
          return;
        }

        if (isPopupSubmitControl(target)) {
          window.setTimeout(() => dismissRolloutPopup(formId), 300);
        }
      },
      true,
    );

    document.addEventListener(
      'submit',
      (event) => {
        const form = /** @type {Element|null} */ (event.target instanceof Element ? event.target : null);
        if (!form || !isInsideRolloutPopup(form, formId)) return;
        window.setTimeout(() => dismissRolloutPopup(formId), 300);
      },
      true,
    );

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'childList') continue;
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          const successEl =
            node.matches('[data-testid*="success" i]') ||
            node.querySelector('[data-testid*="success" i]');
          if (successEl && isInsideRolloutPopup(successEl, formId)) {
            window.setTimeout(() => dismissRolloutPopup(formId), 500);
            return;
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  /** @param {string} formId */
  function maybeAutoOpenPopup(formId, autoOpen) {
    if (!formId || !autoOpen || hasPopupBeenDismissed()) return;

    window.setTimeout(() => {
      openKlaviyoFormWhenReady(formId);
    }, 600);
  }

  function bindNotifyTriggers(formId) {
    document.querySelectorAll('[data-us-rollout-notify]').forEach((trigger) => {
      if (trigger.dataset.usRolloutBound === 'true') return;
      trigger.dataset.usRolloutBound = 'true';
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        if (trigger.closest('[data-us-rollout-notice]')) {
          rolloutNoticeHiddenUntilReload = true;
          applyRolloutUI(lastShowState);
        }
        openKlaviyoFormWhenReady(formId);
      });
    });
  }

  /** @param {boolean} show */
  function applyRolloutUI(show) {
    document.documentElement.classList.toggle('us-rollout-active', show);
    document.documentElement.dataset.usRolloutVisible = show ? 'true' : 'false';

    document.querySelectorAll('[data-us-rollout-banner]').forEach((banner) => {
      banner.hidden = !show;
      banner.setAttribute('aria-hidden', show ? 'false' : 'true');
    });

    document.querySelectorAll('[data-us-rollout-notice]').forEach((notice) => {
      notice.hidden = !show || rolloutNoticeHiddenUntilReload;
    });
  }

  let lastShowState = false;
  let lastFormId = '';
  let rolloutNoticeHiddenUntilReload = false;

  function refreshUI() {
    applyRolloutUI(lastShowState);
    bindNotifyTriggers(lastFormId);
  }

  function refreshProductAddUI() {
    [0, 100, 300, 700].forEach((delay) => {
      window.setTimeout(refreshUI, delay);
    });
  }

  /** @param {Record<string, unknown>} config */
  async function init(config) {
    if (!config || !config.enabled) {
      lastShowState = false;
      applyRolloutUI(false);
      return;
    }

    const supportedStates = parseSupportedStates(String(config.supportedStates || ''));
    const geo = await fetchGeo(config);
    const show = shouldShowRollout(geo, supportedStates);

    const formId = String(config.klaviyoFormId || '');
    lastShowState = show;
    lastFormId = formId;
    applyRolloutUI(show);
    bindNotifyTriggers(formId);

    if (formId) {
      watchPopupDismiss(formId);
    }

    if (show) {
      maybeAutoOpenPopup(formId, config.autoOpenPopup !== false);
    } else if (formId) {
      // Klaviyo may still auto-open via its own display rules; close for supported states.
      closeKlaviyoFormWhenReady(formId);
    }

    document.dispatchEvent(
      new CustomEvent('us-rollout:ready', {
        detail: {
          show,
          geo,
          queryOverride: Boolean(getQueryGeoOverride()),
          supportedStates: Array.from(supportedStates),
        },
      }),
    );
  }

  function bootstrap() {
    const config = getConfig();
    if (!config) return;
    init(config);
  }

  function setupProductAddRefresh() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || event.source !== 'product-form') return;
      refreshProductAddUI();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bootstrap();
      setupProductAddRefresh();
    });
  } else {
    bootstrap();
    setupProductAddRefresh();
  }
})();
